import React, { useEffect, useState } from "react";
import { api, formatEUR, formatDate, apiUrl } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Download, Plus, X, Building2, Send, AlertCircle } from "lucide-react";

export default function Invoicing() {
  const [tab, setTab] = useState("invoices");
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState({ provider: "manual_xml", enabled: false, series: "FE", next_number: 1 });
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [editingCfg, setEditingCfg] = useState(false);
  const [newCust, setNewCust] = useState(null);
  const [newInv, setNewInv] = useState(null);

  const load = () => {
    api.get("/invoicing/providers").then(r => setProviders(Array.isArray(r.data) ? r.data : []));
    api.get("/invoicing/config").then(r => setConfig(r.data));
    api.get("/invoicing/customers").then(r => setCustomers(Array.isArray(r.data) ? r.data : []));
    api.get("/invoicing/invoices").then(r => setInvoices(Array.isArray(r.data) ? r.data : []));
  };
  useEffect(() => { load(); }, []);

  const saveCfg = async (cfg) => {
    try {
      await api.post("/invoicing/config", cfg);
      toast.success("Configurazione salvata");
      setEditingCfg(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const saveCust = async (c) => {
    try { await api.post("/invoicing/customers", c); toast.success("Cliente salvato"); setNewCust(null); load(); }
    catch { toast.error("Errore"); }
  };

  const saveInv = async (inv) => {
    try {
      const r = await api.post("/invoicing/invoices", inv);
      toast.success(`Fattura ${r.data.series}${String(r.data.number).padStart(5, "0")} creata`);
      setNewInv(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const downloadXml = async (inv) => {
    const token = localStorage.getItem("velta_token");
    const r = await fetch(apiUrl(`/invoicing/invoices/${inv.id}/xml`), { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Fattura_${inv.series}${String(inv.number).padStart(5, "0")}.xml`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const sendToSdi = async (inv) => {
    if (!window.confirm(`Trasmettere la fattura ${inv.series}${String(inv.number).padStart(5, "0")} al SDI tramite ${config.provider}?`)) return;
    try {
      const r = await api.post(`/invoicing/invoices/${inv.id}/send-sdi`);
      if (r.data.ok) {
        toast.success(`Fattura inviata al SDI via ${r.data.provider}`);
      } else {
        toast.error(`Errore SDI: ${JSON.stringify(r.data.detail?.response || r.data.detail)?.slice(0, 200)}`);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio SDI");
    }
  };

  const sdiDirectSupported = config.provider === "aruba" || config.provider === "fattura24";

  return (
    <div className="p-8 lg:p-12">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Fatturazione</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Fatturazione Elettronica SDI</h1>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6 flex items-start gap-3 text-sm">
        <AlertCircle className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
        <div className="text-blue-900">
          VELTA genera l&apos;XML FatturaPA conforme. Puoi <strong>scaricare l&apos;XML</strong> (caricalo manualmente sul tuo gestionale SDI), oppure configurare un <strong>provider</strong> per l&apos;<strong>invio diretto a SDI</strong>:
          <span className="font-medium"> Aruba</span> (OAuth2, endpoint <code className="text-xs">uploadSigned/upload</code>) o
          <span className="font-medium"> Fattura24</span> (API key, endpoint <code className="text-xs">SaveDocument</code>). Inserisci le credenziali ufficiali nel pannello &quot;Configura&quot; qui sotto.
        </div>
      </div>

      <div className="bg-white border border-velta-border rounded-md p-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Provider configurato</div>
            <div className="font-heading text-xl font-semibold">
              {providers.find(p => p.id === config.provider)?.label || "Nessuno"}
            </div>
            <div className="text-sm text-velta-mute mt-1">
              Serie: <span className="font-mono">{config.series}</span> · Prossimo numero: <span className="font-mono">{String(config.next_number).padStart(5, "0")}</span>
            </div>
          </div>
          <button data-testid="inv-edit-cfg-btn" onClick={() => setEditingCfg(true)} className="h-10 px-4 bg-velta-ink text-white rounded-md text-sm font-medium btn-tactile">Configura</button>
        </div>
      </div>

      <div className="border-b border-velta-border mb-6 flex gap-1">
        {[{ id: "invoices", l: "Fatture" }, { id: "customers", l: "Clienti" }].map(t => (
          <button key={t.id} data-testid={`inv-tab-${t.id}`} onClick={() => setTab(t.id)} className={`h-12 px-5 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-[#1E2D5B] text-[#1E2D5B]" : "border-transparent text-velta-mute hover:text-velta-ink"}`}>
            {t.l}
          </button>
        ))}
      </div>

      {tab === "invoices" && (
        <>
          <div className="mb-4 flex justify-end">
            <button data-testid="inv-new-btn" onClick={() => setNewInv({ customer_id: customers[0]?.id || "", lines: [{ name: "", qty: 1, price: 0, vat: 22 }] })} className="h-11 px-4 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
              <Plus className="w-4 h-4" /> Nuova fattura
            </button>
          </div>
          <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
            <table className="w-full text-sm min-w-[760px]">
              <thead className="bg-velta-bg border-b border-velta-border">
                <tr className="text-left text-xs font-mono uppercase tracking-widest text-velta-mute">
                  <th className="px-5 py-4">Numero</th>
                  <th className="px-5 py-4">Cliente</th>
                  <th className="px-5 py-4">Data</th>
                  <th className="px-5 py-4 text-right">Totale</th>
                  <th className="px-5 py-4">Stato</th>
                  <th className="px-5 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(invoices) ? invoices : []).map(inv => (
                  <tr key={inv.id} className="border-b border-velta-border last:border-0 hover:bg-velta-bg">
                    <td className="px-5 py-4 font-mono font-medium">{inv.series}{String(inv.number).padStart(5, "0")}</td>
                    <td className="px-5 py-4">{inv.customer_snapshot?.name}</td>
                    <td className="px-5 py-4 text-velta-mute text-xs">{formatDate(inv.created_at)}</td>
                    <td className="px-5 py-4 text-right font-mono font-bold">{formatEUR(inv.total)}</td>
                    <td className="px-5 py-4 text-xs">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3 justify-end">
                        <button data-testid={`inv-xml-${inv.id}`} onClick={() => downloadXml(inv)} className="text-[#1E2D5B] hover:underline text-xs flex items-center gap-1 font-medium">
                          <Download className="w-3.5 h-3.5" /> XML
                        </button>
                        {sdiDirectSupported && inv.status !== "sent_to_sdi" && (
                          <button data-testid={`inv-send-sdi-${inv.id}`} onClick={() => sendToSdi(inv)} className="text-[#2F9E77] hover:underline text-xs flex items-center gap-1 font-medium">
                            <Send className="w-3.5 h-3.5" /> Invia SDI
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {invoices.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-velta-mute">Nessuna fattura emessa.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "customers" && (
        <>
          <div className="mb-4 flex justify-end">
            <button data-testid="inv-new-cust-btn" onClick={() => setNewCust({ name: "", country: "IT" })} className="h-11 px-4 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
              <Plus className="w-4 h-4" /> Nuovo cliente
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {(Array.isArray(customers) ? customers : []).map(c => (
              <div key={c.id} className="bg-white border border-velta-border rounded-md p-5">
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-velta-subtle shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold">{c.name}</div>
                    <div className="text-xs text-velta-mute font-mono mt-1">
                      {c.vat_number && <div>P.IVA: {c.vat_number}</div>}
                      {c.sdi_code && <div>SDI: {c.sdi_code}</div>}
                      {c.pec && <div>PEC: {c.pec}</div>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {customers.length === 0 && <div className="col-span-2 p-12 text-center text-velta-mute bg-white border border-velta-border rounded-md">Nessun cliente. Aggiungi il primo.</div>}
          </div>
        </>
      )}

      {editingCfg && <ConfigModal cfg={config} providers={providers} onClose={() => setEditingCfg(false)} onSave={saveCfg} />}
      {newCust && <CustomerModal cust={newCust} onClose={() => setNewCust(null)} onSave={saveCust} />}
      {newInv && <InvoiceModal inv={newInv} customers={customers} onClose={() => setNewInv(null)} onSave={saveInv} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    draft: { c: "bg-gray-100 text-gray-700", l: "Bozza" },
    queued: { c: "bg-amber-100 text-amber-800", l: "In coda" },
    sent_to_sdi: { c: "bg-emerald-100 text-emerald-800", l: "Inviata a SDI" },
    accepted_sdi: { c: "bg-emerald-100 text-emerald-800", l: "Accettata SDI" },
    sdi_error: { c: "bg-red-100 text-red-700", l: "Errore SDI" },
    sent_to_provider_MOCKED: { c: "bg-amber-100 text-amber-800", l: "Inviata (mock)" },
  };
  const s = map[status] || { c: "bg-gray-100 text-gray-700", l: status || "—" };
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${s.c}`}>{s.l}</span>;
}

function ConfigModal({ cfg, providers, onClose, onSave }) {
  const [f, setF] = useState(cfg);
  const isAruba = f.provider === "aruba";
  const isFattura24 = f.provider === "fattura24";
  const supportsDirect = isAruba || isFattura24;
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h3 className="font-heading text-xl font-semibold">Configura provider SDI</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Provider</label>
            <div className="space-y-2">
              {(Array.isArray(providers) ? providers : []).map(p => (
                <label key={p.id} data-testid={`inv-cfg-provider-${p.id}`} className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${f.provider === p.id ? "border-[#1E2D5B] bg-[#1E2D5B]/5" : "border-velta-border hover:border-velta-ink"}`}>
                  <input type="radio" name="provider" checked={f.provider === p.id} onChange={() => setF({ ...f, provider: p.id })} />
                  <div className="flex-1">
                    <div className="font-medium text-sm">{p.label}</div>
                    {p.api_url && <div className="text-xs text-velta-mute font-mono">{p.api_url}</div>}
                    {(p.id === "aruba" || p.id === "fattura24") && (
                      <div className="text-[11px] text-emerald-700 font-medium mt-1">✓ Invio diretto SDI supportato</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
          {isAruba && (
            <>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Username Aruba</label>
                <input data-testid="inv-cfg-username" value={f.sender_id || ""} onChange={(e) => setF({ ...f, sender_id: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" placeholder="user_demo" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Password Aruba</label>
                <input data-testid="inv-cfg-secret" type="password" value={f.api_secret || ""} onChange={(e) => setF({ ...f, api_secret: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" placeholder="••••••" />
              </div>
            </>
          )}
          {isFattura24 && (
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">API Key Fattura24</label>
              <input data-testid="inv-cfg-apikey" value={f.api_key || ""} onChange={(e) => setF({ ...f, api_key: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" placeholder="La tua API key di Fattura24" />
              <div className="text-[11px] text-velta-mute mt-1">Recupera la chiave da Fattura24 → Impostazioni → API.</div>
            </div>
          )}
          {f.provider && f.provider !== "manual_xml" && !isAruba && !isFattura24 && (
            <>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">API Key / Username</label>
                <input value={f.api_key || ""} onChange={(e) => setF({ ...f, api_key: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">API Secret / Password</label>
                <input type="password" value={f.api_secret || ""} onChange={(e) => setF({ ...f, api_secret: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" placeholder="Lasciate vuoto per non modificare" />
              </div>
            </>
          )}
          {supportsDirect && (
            <div className="bg-velta-bg border border-velta-border rounded-md p-3 space-y-2">
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="inv-cfg-sandbox" type="checkbox" checked={!!f.sandbox} onChange={(e) => setF({ ...f, sandbox: e.target.checked })} className="w-4 h-4" />
                <div>
                  <div className="text-sm font-medium">Modalità sandbox (test)</div>
                  <div className="text-[11px] text-velta-mute">Usa gli endpoint demo del provider per i test prima della produzione.</div>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="inv-cfg-autosend" type="checkbox" checked={!!f.auto_send} onChange={(e) => setF({ ...f, auto_send: e.target.checked })} className="w-4 h-4" />
                <div>
                  <div className="text-sm font-medium">Invio automatico al SDI</div>
                  <div className="text-[11px] text-velta-mute">Trasmette al SDI subito dopo la creazione della fattura. Disattiva per invio manuale.</div>
                </div>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="inv-cfg-enabled" type="checkbox" checked={!!f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} className="w-4 h-4" />
                <div className="text-sm font-medium">Provider abilitato</div>
              </label>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Serie</label>
              <input value={f.series || "FE"} onChange={(e) => setF({ ...f, series: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Prossimo numero</label>
              <input type="number" value={f.next_number || 1} onChange={(e) => setF({ ...f, next_number: parseInt(e.target.value) || 1 })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
            </div>
          </div>
        </div>
        <button data-testid="inv-cfg-save-btn" onClick={() => onSave(f)} className="w-full mt-6 h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">Salva</button>
      </div>
    </div>
  );
}

function CustomerModal({ cust, onClose, onSave }) {
  const [f, setF] = useState(cust);
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h3 className="font-heading text-xl font-semibold">Nuovo cliente fatturazione</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="space-y-3">
          <input data-testid="inv-cust-name" required placeholder="Ragione sociale *" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="P.IVA" value={f.vat_number || ""} onChange={(e) => setF({ ...f, vat_number: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
            <input placeholder="Codice Fiscale" value={f.fiscal_code || ""} onChange={(e) => setF({ ...f, fiscal_code: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="inv-cust-sdi" placeholder="Codice SDI (7 caratteri)" maxLength={7} value={f.sdi_code || ""} onChange={(e) => setF({ ...f, sdi_code: e.target.value.toUpperCase() })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
            <input placeholder="PEC" value={f.pec || ""} onChange={(e) => setF({ ...f, pec: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </div>
          <input placeholder="Indirizzo" value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          <div className="grid grid-cols-3 gap-3">
            <input placeholder="CAP" value={f.zip_code || ""} onChange={(e) => setF({ ...f, zip_code: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
            <input placeholder="Città" value={f.city || ""} onChange={(e) => setF({ ...f, city: e.target.value })} className="h-11 col-span-2 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
          </div>
          <input placeholder="Provincia (MI)" maxLength={2} value={f.province || ""} onChange={(e) => setF({ ...f, province: e.target.value.toUpperCase() })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
        </div>
        <button data-testid="inv-cust-save" onClick={() => onSave(f)} className="w-full mt-6 h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">Salva cliente</button>
      </div>
    </div>
  );
}

function InvoiceModal({ inv, customers, onClose, onSave }) {
  const [f, setF] = useState(inv);
  const setLine = (i, k, v) => { const ls = [...f.lines]; ls[i] = { ...ls[i], [k]: k === "name" ? v : (parseFloat(v) || 0) }; setF({ ...f, lines: ls }); };
  const addLine = () => setF({ ...f, lines: [...f.lines, { name: "", qty: 1, price: 0, vat: 22 }] });
  const delLine = (i) => { const ls = [...f.lines]; ls.splice(i, 1); setF({ ...f, lines: ls }); };
  const total = f.lines.reduce((s, l) => s + (l.price || 0) * (l.qty || 0), 0);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50 overflow-auto" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-2xl p-6 my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5"><h3 className="font-heading text-xl font-semibold">Nuova fattura</h3><button onClick={onClose}><X className="w-5 h-5" /></button></div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Cliente</label>
            <select data-testid="inv-new-customer" value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]">
              <option value="">— seleziona —</option>
              {(Array.isArray(customers) ? customers : []).map(c => <option key={c.id} value={c.id}>{c.name} {c.vat_number ? `(${c.vat_number})` : ""}</option>)}
            </select>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-mono uppercase tracking-widest text-velta-mute">Righe (prezzi lordi IVA inclusa)</label>
              <button onClick={addLine} className="text-xs font-medium text-[#1E2D5B]">+ Aggiungi riga</button>
            </div>
            {(Array.isArray(f.lines) ? f.lines : []).map((l, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 mb-2">
                <input placeholder="Descrizione" value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} className="h-10 col-span-5 bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B] text-sm" />
                <input type="number" step="0.01" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} className="h-10 col-span-2 bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B] text-sm" placeholder="Qta" />
                <input type="number" step="0.01" value={l.price} onChange={(e) => setLine(i, "price", e.target.value)} className="h-10 col-span-2 bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B] text-sm" placeholder="Prezzo" />
                <select value={l.vat} onChange={(e) => setLine(i, "vat", e.target.value)} className="h-10 col-span-2 bg-white border border-velta-border rounded-md px-2 font-mono outline-none focus:border-[#1E2D5B] text-sm">
                  <option value={4}>4%</option><option value={5}>5%</option><option value={10}>10%</option><option value={22}>22%</option>
                </select>
                <button onClick={() => delLine(i)} className="col-span-1 text-velta-error"><X className="w-4 h-4 mx-auto" /></button>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-velta-border">
            <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">Totale lordo</span>
            <span className="font-mono font-bold text-2xl">{formatEUR(total)}</span>
          </div>
        </div>
        <button data-testid="inv-save-btn" onClick={() => onSave(f)} disabled={!f.customer_id || !f.lines.length} className="w-full mt-6 h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center justify-center gap-2 btn-tactile disabled:opacity-40">
          <Send className="w-4 h-4" /> Emetti fattura
        </button>
      </div>
    </div>
  );
}
